import React, { useState, useEffect, useCallback, use } from 'react';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls'; // <-- import activeDevice
import DeviceMap from './components/DeviceMap';
import {
  Box,
  SimpleGrid,
  Text,
  Button,
  useColorModeValue,
  useDisclosure,
  Icon,
  useToast,
  Flex,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdDevices, MdCheckCircle, MdCancel, MdAlarm } from 'react-icons/md';

import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import ComplexTable from 'views/admin/default/components/ComplexTable';
import EditDeviceDetailModal from 'components/modal/Modal';
import { Switch } from '@chakra-ui/react';

export default function MainDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  const [deviceData, setDeviceData] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [savingDevice, setSavingDevice] = useState(false);
  const [devicesArray, setDevicesArray] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDevices, setTotalDevices] = useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();

  // const fetchConfigurations = useCallback(async (deviceName) => {
  //   try {
  //     const keys =
  //       'active,warranty_date,location,app_version,device_type,patch_v';
  //     const resp = await axiosInstance.get(
  //       apiUrls.factorytoolgroupAttributes(deviceName, keys),
  //     );

  //     console.log('fetchConfigurations response:', resp);
  //   } catch (error) {
  //     console.error('Error fetching configurations:', error);
  //   }
  // }, []);

  const fetchDeviceAttributes = useCallback(async (deviceId, deviceName) => {
    const keys =
      'active,warranty_date,location,app_version,device_type,patch_v';
    try {
      const resp = await axiosInstance.get(
        apiUrls.factorytoolgroupAttributes(deviceName, keys),
      );

      const attrs = resp.data;
      const attrMap = Object.fromEntries(attrs.map((a) => [a.key, a.value]));

      return {
        active: attrMap.active ?? false,
        warranty: attrMap.warranty_date ?? null,
        app_version: attrMap.app_version ?? 'N/A',
        device_type: attrMap.device_type ?? 0,
        patch_v: attrMap.patch_v ?? 'N/A',
      };
    } catch (error) {
      if (error?.response?.status === 404) {
        // console.log('404 error in fetchDeviceAttributes:', error);
      } else {
        throw error;
      }
      return {
        active: false,
        warranty: null,
        app_version: 'N/A',
        device_type: 0,
        patch_v: 'N/A',
      };
    }
  }, []);

  const logActiveDevice = useCallback(async () => {
    try {
      const resp = await axiosInstance.get(apiUrls.activeDevices(false));
      // console.log('activeDevice response:', resp);
    } catch (error) {
      console.error('Error in activeDevice:', error);
    }
  }, []);

  useEffect(() => {
    logActiveDevice();
  }, [logActiveDevice]);
  const fetchAllDevices = useCallback(
    async (serialNumber = '', setLoading = true, page = 0) => {
      setLoadingDevices(setLoading);

      try {
        const resp = await axiosInstance.get(
          apiUrls.allDevices(serialNumber, page),
        );

        const devices = resp.data.data;
        setHasNext(resp.data.hasNext);
        if (typeof resp.data.totalPages !== 'undefined') {
          setTotalPages(resp.data.totalPages);
        }
        if (typeof resp.data.totalElements !== 'undefined') {
          setTotalDevices((prev) =>
            prev === 0 ? resp.data.totalElements : prev,
          );
        } else {
          setTotalDevices((prev) => (prev === 0 ? devices.length : prev));
        }

        const temp = await Promise.all(
          devices.map(async (d) => {
            const attrs = await fetchDeviceAttributes(d.id.id, d.name);
            return {
              deviceId: d?.id?.id,
              machineModel: d.label || '',
              serialNumber: d.name,
              name: d.name,
              ...attrs,
              details: {
                text: 'Configure',
                id: d.id.id,
                machineSerialNumber: d.name,
              },
              warranty: attrs.warranty,
            };
          }),
        );
        // console.log('temp-----------------------', temp);

        setDeviceData(temp);
      } catch (error) {
        if (error?.response?.status === 404) {
          console.log('404 error in fetchAllDevices:', error);
        } else {
          // Optionally, you can show a toast for non-404 errors here if needed
          toast({
            title: 'Error',
            description: 'Failed to fetch devices. Please try again.',
            status: 'error',
            duration: 4000,
            isClosable: true,
            position: 'center',
          });
        }
      } finally {
        setLoadingDevices(false);
      }
    },
    [fetchDeviceAttributes, toast],
  );

  useEffect(() => {
    fetchAllDevices();
  }, [fetchAllDevices]);

  const saveDeviceInfo = useCallback(
    async (updatedDevice) => {
      setSavingDevice(true);

      try {
        const warrantyDate =
          typeof updatedDevice.warranty === 'string'
            ? new Date(updatedDevice.warranty).getTime()
            : updatedDevice.warranty;

        const data = {
          warranty_date: warrantyDate,
          location: {
            lat: updatedDevice.location.lat,
            lang: updatedDevice.location.lng,
            loc: updatedDevice.location.loc || '',
          },
        };

        const response = await axiosInstance.post(
          apiUrls.updateDevice(updatedDevice.id),
          data,
        );

        if (response.status === 200) {
          toast({
            title: 'Device updated.',
            description:
              'The device information has been updated successfully.',
            status: 'success',
            duration: 4000,
            isClosable: true,
            position: 'center',
          });

          await fetchAllDevices();
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          console.log('404 error in saveDeviceInfo:', error);
        } else {
          toast({
            title: 'Error',
            description: 'Failed to update device info. Please try again.',
            status: 'error',
            duration: 4000,
            isClosable: true,
            position: 'center',
          });
        }
      } finally {
        setSavingDevice(false);
      }
    },
    [toast, fetchAllDevices],
  );

  const handleSave = useCallback(
    (updatedDevice) => {
      saveDeviceInfo(updatedDevice);
      onClose();
    },
    [onClose, saveDeviceInfo],
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return 'NA';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = React.useMemo(
    () => [
      // {
      //   id: 'machineModel',
      //   accessorKey: 'machineModel',
      //   header: () => <Text>Machine Model</Text>,
      //   cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      // },
      {
        id: 'serialNumber',
        accessorKey: 'serialNumber',
        header: () => <Text>Serial Number</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'active',
        accessorKey: 'active',
        header: () => <Text>Status</Text>,
        cell: (info) => (
          <Text color={info.getValue() ? 'green.500' : 'red.500'}>
            {typeof info.getValue() === 'undefined' || info.getValue() === null
              ? '-'
              : info.getValue()
              ? 'Active'
              : 'Inactive'}
          </Text>
        ),
      },
      // {
      //   id: 'location',
      //   accessorKey: 'location',
      //   header: () => <Text>Location</Text>,
      //   cell: (info) => {
      //     const loc = info.getValue();
      //     return <Text>{loc?.loc || 'NA'}</Text>;
      //   },
      // },
      // {
      //   id: 'warranty',
      //   accessorKey: 'warranty',
      //   header: () => <Text>Warranty</Text>,
      //   cell: (info) => <Text>{formatDate(info.getValue())}</Text>,
      // },
      {
        id: 'app_version',
        accessorKey: 'app_version',
        header: () => <Text>App Version</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'device_type',
        accessorKey: 'device_type',
        header: () => <Text>Device Type</Text>,
        cell: (info) => {
          const type = info.getValue();
          let label = '-';
          if (type === 1) label = 'MINI';
          else if (type === 2) label = 'GRIDX';
          else if (type === 3) label = 'MINI SECURE';
          return <Text>{label}</Text>;
        },
      },
      {
        id: 'patch_v',
        accessorKey: 'patch_v',
        header: () => <Text>Patch Version</Text>,
        cell: (info) => {
          const value = info.getValue();
          return <Text>{value === 0 ? 0 : value || '-'}</Text>;
        },
      },
      {
        id: 'configure',
        accessorKey: 'details',
        header: () => <Text>Configure</Text>,
        cell: (info) => (
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            borderWidth="2px"
            borderColor="blue.200"
            color="blue.400"
            cursor="pointer"
            disabled={loadingDevices}
            onClick={() => {
              // store the deviceId in localStorage before navigating
              try {
                localStorage.setItem(
                  'selectedDeviceId',
                  String(info.row.original.deviceId),
                );
                localStorage.setItem(
                  'deviceId',
                  String(info.row.original.serialNumber),
                );
                localStorage.setItem(
                  'deviceType',
                  String(info.row.original.device_type),
                );
              } catch (e) {
                // ignore storage errors
              }
              console.log(
                'Navigating with deviceId:',
                info.row.original.serialNumber,
              );
              navigate(`/device/${info.row.original.deviceId}/edit`, {
                state: {
                  deviceId: info.row.original.serialNumber,
                },
              });
            }}
          >
            {info.getValue()?.text || '-'}
          </Button>
        ),
      },
      // {
      //   id: 'edit',
      //   header: () => <Text>Edit</Text>,
      //   cell: (info) => (
      //     <Button
      //       size="sm"
      //       colorScheme="green"
      //       variant="outline"
      //       borderWidth="2px"
      //       borderColor="green.200"
      //       color="green.500"
      //       cursor="pointer"
      //       onClick={() => {
      //         setSelectedDevice(info.row.original);
      //         onOpen();
      //         // Call fetchConfigurations with the device name
      //         fetchConfigurations(info.row.original.name);
      //       }}
      //       isLoading={savingDevice}tot
      //     >
      //       Edit
      //     </Button>
      //   ),
      // },
    ],
    [onOpen, loadingDevices, savingDevice, navigate],
  );

  // Derived statistics from deviceData
  // const totalDevices = deviceData.length;
  const activeDevices = deviceData.filter((d) => d.active).length;
  const inactiveDevices = totalDevices - activeDevices;
  const currentTime = Date.now();
  const warrantyExpired = deviceData.filter(
    (d) => d.warranty && d.warranty < currentTime,
  ).length;
  const warrantyActive = deviceData.filter(
    (d) => d.warranty && d.warranty >= currentTime,
  ).length;

  return (
    <Box p={6} ms={4}>
      <SimpleGrid
        columns={{ base: 1, md: 3, lg: 5, '2xl': 5 }}
        gap="20px"
        mb="20px"
      >
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={
                <Icon as={MdDevices} color={brandColor} w="32px" h="32px" />
              }
            />
          }
          name="Total Devices"
          value={totalDevices}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={
                <Icon as={MdCheckCircle} color="green.400" w="32px" h="32px" />
              }
            />
          }
          name="Active Devices"
          value={activeDevices}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon as={MdCancel} color="red.400" w="32px" h="32px" />}
            />
          }
          name="Inactive Devices"
          value={inactiveDevices}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon as={MdAlarm} color="orange.400" w="32px" h="32px" />}
            />
          }
          name="Warranty Expired"
          value={warrantyExpired}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={
                <Icon as={MdCheckCircle} color="blue.400" w="32px" h="32px" />
              }
            />
          }
          name="Warranty Active"
          value={warrantyActive}
        />
      </SimpleGrid>
      <Flex gap={4} alignItems="center" justifyContent="end" mb="4" w="100%">
        <Text fontSize="sm" color={brandColor} fontWeight="bold">
          {showMap ? ' ' : 'Show Map'}
        </Text>
        {/* <Switch
          id="toggle-mode"
          isChecked={showMap}
          onChange={(e) => setShowMap(e.target.checked)}
          colorScheme="purple"
        /> */}
      </Flex>

      <Flex gap={6} direction={{ base: 'column', lg: 'row' }} w="100%">
        {/* Table Section */}
        <Box flex="3">
          <ComplexTable
            columnsData={columns}
            tableData={deviceData}
            loading={loadingDevices}
            fetchAllDevices={fetchAllDevices}
            hasNext={hasNext}
            totalPages={totalPages}
          />
        </Box>

        {/* Map Section */}
        {/* {showMap && (
          <Box flex="1" minW={{ base: '100%', lg: '300px' }}>
            <DeviceMap devices={deviceData} />
          </Box>
        )} */}
      </Flex>

      <EditDeviceDetailModal
        initialData={selectedDevice || {}}
        isOpen={isOpen}
        onClose={onClose}
        onSave={handleSave}
      />
    </Box>
  );
}
